/**
 * WorkspaceModal Component - Create/Edit Workspace
 *
 * Self-contained modal that handles both create and edit workspace operations.
 * Reads store state directly for mode detection (no props needed).
 *
 * @module components/workspace/WorkspaceModal
 */

import type { FormEvent } from 'react';
import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, Button, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AppTextInput } from '@/components/common/AppTextInput';
import { AppTextarea } from '@/components/common/AppTextarea';
import { FormFooter } from '@/components/common/form';
import { useGeoMarkupStore } from '@/store';
import {
  selectIsCreateModalOpen,
  selectIsEditModalOpen,
  selectEditModalWorkspaceId,
} from '@/store/selectors';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useWorkspaceForm } from '@/hooks/useWorkspaceForm';
import { useWorkspaceSwitcher } from '@/hooks/useWorkspaceSwitcher';
import {
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_NAME_MIN_LENGTH,
  WORKSPACE_DESCRIPTION_MAX_LENGTH,
} from '@/constants/workspace';
import { TEXTAREA_MAX_ROWS, TEXTAREA_MIN_ROWS } from '@/constants/ui';
import styles from './WorkspaceModal.module.css';

export const WorkspaceModal = memo(function WorkspaceModal() {
  const { t } = useTranslation();
  const { createWorkspace, updateWorkspace, workspaces, isCreating } =
    useWorkspace();
  // Navigate to the newly created workspace's dashboard so the URL (source of
  // truth for AppShell) matches the active workspace; without this the AppShell
  // URL-sync effect reverts the selection back to the previous workspace.
  const switchToWorkspace = useWorkspaceSwitcher('dashboard');

  // ===== ATOMIC STORE SUBSCRIPTIONS =====
  const isCreateOpen = useGeoMarkupStore(selectIsCreateModalOpen);
  const isEditOpen = useGeoMarkupStore(selectIsEditModalOpen);
  const editWorkspaceId = useGeoMarkupStore(selectEditModalWorkspaceId);
  const closeCreateModal = useGeoMarkupStore(
    (state) => state.workspace.closeCreateModal
  );
  const closeEditModal = useGeoMarkupStore(
    (state) => state.workspace.closeEditModal
  );

  // ===== SHARED FORM STATE & VALIDATION =====
  const {
    name,
    description,
    errors,
    setName,
    setDescription,
    validateForm,
    handleNameChange,
    handleNameBlur,
    handleDescriptionChange,
    resetForm,
  } = useWorkspaceForm();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ===== DERIVED STATE =====
  const opened = isCreateOpen || isEditOpen;
  const isEditMode = isEditOpen;

  const isFormValid = useMemo(() => {
    const trimmedLength = name.trim().length;
    return (
      trimmedLength >= WORKSPACE_NAME_MIN_LENGTH &&
      name.length <= WORKSPACE_NAME_MAX_LENGTH &&
      description.length <= WORKSPACE_DESCRIPTION_MAX_LENGTH
    );
  }, [name, description]);

  // ===== HANDLERS =====
  const handleClose = useCallback(() => {
    if (isCreateOpen) {
      closeCreateModal();
    } else if (isEditOpen) {
      closeEditModal();
    }
    resetForm();
    setIsSubmitting(false);
  }, [isCreateOpen, isEditOpen, closeCreateModal, closeEditModal, resetForm]);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        const workspaceData = {
          name: name.trim(),
          description: description.trim() || undefined,
        };

        if (isEditMode && editWorkspaceId) {
          const result = await updateWorkspace(editWorkspaceId, workspaceData);
          if (result.success) {
            handleClose();
          }
        } else {
          const result = await createWorkspace(workspaceData);
          if (result.success) {
            handleClose();
            switchToWorkspace(result.workspaceId, result.shortId);
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      isEditMode,
      editWorkspaceId,
      name,
      description,
      updateWorkspace,
      createWorkspace,
      switchToWorkspace,
      handleClose,
    ]
  );

  // ===== EFFECTS =====

  // Populate form when editing
  useEffect(() => {
    if (isEditOpen && editWorkspaceId) {
      const workspace = workspaces.find(
        (item) => item.workspaceId === editWorkspaceId
      );
      if (workspace) {
        setName(workspace.name);
        setDescription(workspace.description ?? '');
      }
    }
  }, [isEditOpen, editWorkspaceId, workspaces, setName, setDescription]);

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      resetForm();
      setIsSubmitting(false);
    }
  }, [opened, resetForm]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        isEditMode
          ? t('workspace.modal.editTitle')
          : t('workspace.modal.createTitle')
      }
      centered
      size="md"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <Stack gap="md">
          <AppTextInput
            label={t('workspace.form.name')}
            placeholder={t('workspace.form.namePlaceholder')}
            value={name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            error={errors.name}
            maxLength={WORKSPACE_NAME_MAX_LENGTH}
            required
            disabled={isSubmitting}
            data-autofocus
          />
          <AppTextarea
            label={t('workspace.form.description')}
            placeholder={t('workspace.form.descriptionPlaceholder')}
            value={description}
            onChange={handleDescriptionChange}
            error={errors.description}
            maxLength={WORKSPACE_DESCRIPTION_MAX_LENGTH}
            minRows={TEXTAREA_MIN_ROWS}
            maxRows={TEXTAREA_MAX_ROWS}
            autosize
            disabled={isSubmitting}
          />
          <FormFooter
            leftActions={
              <Button
                variant="subtle"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
            }
            rightActions={
              <Button
                type="submit"
                loading={isSubmitting || isCreating}
                disabled={!isFormValid}
              >
                {isEditMode
                  ? t('workspace.modal.save')
                  : t('workspace.form.create')}
              </Button>
            }
          />
        </Stack>
      </form>
    </Modal>
  );
});
